import { connect } from 'react-redux';
import {
  worklistMapDispatchToProps,
  worklistMapStateToProps
} from './service-desk-component';
import {
  mapUpdatedDispatchToCodeBenchProps,
  mapUpdatedStateToCodeBenchProps
} from '../../views/accelerate/code-bench-component';
import { ServiceDeskAndGradeBench } from './service-desk-and-gradebench-component';
import {
  codeBenchActions,
  gradeBenchPanelActions,
  conditionalUtils,
  spanSelectors,
  CQA_QUALITY_ASSESSMENT_ASSIGNED_TO_ME_STATUS,
  commonEnums,
  AmplifyCore
} from '@codametrix/ui-common';
import {
  launchWorkitemInAmplify,
  getPrevOrNextCase
} from '../../actions/cases.action';
import { buildQualityAssessmentWorklistPath, launchPageActions } from '@codametrix/ui-common';
import {
  getIsAuditor,
  getSubmitButtonConfig,
  getUserRolesFromActiveContext
} from '../../core/cqa/CQASubmitButton';
import history from '../../history';
import { augmentCursorAPIContextForCQAGradingScreen } from '../../core/cqa/augment-cursor-api-context';

const { selectSpanAnnotations } = spanSelectors;
const { disabledCapability, enabledCapability } = conditionalUtils;

const stateMapper = (state: CMx.ApplicationState, ownProps: any) => {
  const roles = getUserRolesFromActiveContext(state.ui.context);

  const isAuditor = getIsAuditor(
    state.gradeBenchPanel,
    roles,
    state.gradeBenchPanel.workerInfo?.worker_id ?? null
  );

  const associatedWorkitems = state.gradeBenchPanel.associatedWorkitems;

  const baseDetailProps = mapUpdatedStateToCodeBenchProps(state);
  
  // Grab the fresh state from React Router's props, fallback to history
  const locationState = ownProps?.location?.state ?? history.location.state;
  if (locationState) {
    baseDetailProps.cursorAPIContext = { ...baseDetailProps.cursorAPIContext, ...locationState };
  }

  const pathname = ownProps?.location?.pathname ?? history.location.pathname ?? '';
  const cursorAPIContext = augmentCursorAPIContextForCQAGradingScreen(
    state,
    pathname,
    baseDetailProps.cursorAPIContext
  );
  
  const hideCompareMultiWorkitemControls =
    cursorAPIContext?.worklistContext?.selectedStatus ===
    CQA_QUALITY_ASSESSMENT_ASSIGNED_TO_ME_STATUS;

  const actionBarSelectMultipleWorkitems =
    isAuditor &&
    associatedWorkitems.length > 1 &&
    !hideCompareMultiWorkitemControls
      ? enabledCapability
      : disabledCapability;

  const viewScoreDetail = AmplifyCore.benchEditable.getViewScoreDetailCapability(roles);

  const nextVersionButtonConfig = {
    text: 'Next Version',
    tooltip: 'Navigate to next version of the work item'
  };

  const submitButtonConfig = getSubmitButtonConfig(
    state.gradeBenchPanel,
    roles,
    state.gradeBenchPanel.workerInfo?.worker_id ?? null
  );

  const annotatedNotes = state.codeBench.notes.map(note => ({
    ...note,
    spanAnnotations: selectSpanAnnotations(state.gradeBenchPanel)
  }));

  return {
    listProps: worklistMapStateToProps(state),
    detailProps: {
      ...baseDetailProps,
      cursorAPIContext,
      activity: state.gradeBenchPanel.activity,
      gradeableInvoices: state.gradeBenchPanel.invoices,
      fieldDefinitions: state.gradeBenchPanel.fieldDefinitions,
      qaRuleCollection: {
        sampleReason: state.gradeBenchPanel.qaRule.data.sample_reason,
        guideline: state.gradeBenchPanel.qaRule.data.guideline,
        project_configuration:
          state.gradeBenchPanel.qaRule.data.project_configuration
      },
      capabilities: {
        ...state.capabilities,
        capabilities: {
          ...state.capabilities.capabilities,
          servicedeskAction: enabledCapability,
          submitCase: enabledCapability,
          actionBarSelectMultipleWorkitems: actionBarSelectMultipleWorkitems,
          viewScoreDetail: viewScoreDetail
        }
      },
      userContext: state.ui.context.activeContext,
      riskCategories: state.gradeBenchPanel.riskCategories,
      cqaCodeAssessmentRiskDictionaries:
        state.gradeBenchPanel.cqaCodeAssessmentRiskDictionaries,
      gradingErrors: state.gradeBenchPanel.gradingErrors,
      validationResult: state.gradeBenchPanel.validationResult ?? null,
      resolverValidationErrors: state.gradeBenchPanel.resolverValidationErrors ?? null,
      submitButtonConfig: submitButtonConfig,
      nextVersionButtonConfig: nextVersionButtonConfig,
      associatedWorkitems: state.gradeBenchPanel.associatedWorkitems,
      notes: annotatedNotes
    }
  };
};
const dualDispatchMapper = (dispatch: any, ownProps: any) => {
  return {
    listMethods: {
      ...worklistMapDispatchToProps(dispatch),
      openAmplifyCase: (launchConfig: CMxCommonApp.LaunchWorkItemConfig) => {
        dispatch(launchWorkitemInAmplify(launchConfig));
        dispatch(codeBenchActions.hideSideNavBar());
      }
    },
    detailMethods: {
      ...mapUpdatedDispatchToCodeBenchProps(dispatch),
      onGradeUpdate: (update: CMxCommonApp.GradeBenchSelectEvent) => {
        dispatch(gradeBenchPanelActions.gradeUpdate(update));
      },
      fetchGradePanelConfigurations: () => {
        dispatch(gradeBenchPanelActions.fetchRiskCategories());
      },
      onCodeSearchSelection: (update: Accelerate.CodeAddActionPayload) => {
        dispatch(gradeBenchPanelActions.addCode(update))
          .then(() => dispatch(gradeBenchPanelActions.revalidateWorkItem()));
      },
      onGradeRemove: (update: Accelerate.DeleteCQACodeParams) => {
        dispatch(gradeBenchPanelActions.deleteCQACode(update))
          .then(() => dispatch(gradeBenchPanelActions.revalidateWorkItem()));
      },
      onCodeEvent: (update: CMxCommonApp.GradeBenchCodeEvent) => {
        dispatch(gradeBenchPanelActions.resequenceCode(update));
      },
      fetchQaRule: (update: Accelerate.FetchQaRuleParams) => {
        dispatch(gradeBenchPanelActions.fetchQaRule(update));
      },
      onCursorAction: async (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload
      ) => {
        // Ensure we have the freshest context from the router if available
        const locationState = ownProps?.location?.state ?? history.location.state;
        if (locationState && !cursorAPIPayload.cursorAPIContext?.worklistContext) {
          cursorAPIPayload.cursorAPIContext = {
            ...cursorAPIPayload.cursorAPIContext,
            ...locationState
          };
        }
        
        const { cursorAPIContext, operation } = cursorAPIPayload;
        if (cursorAPIContext?.worklistContext?.query == null) {
          if (
            operation === 'NEXT' ||
            operation === 'PREVIOUS' ||
            operation === commonEnums.CursorOperation.NEXT ||
            operation === commonEnums.CursorOperation.PREVIOUS
          ) {
            // If there's no query, we can't fetch the next/prev case.
            // We just return to the worklist.
            const url = buildQualityAssessmentWorklistPath(cursorAPIContext);
            dispatch(launchPageActions.navigationIntent({ intent: 'CASE_DETAIL', url }));
          }
        } else {
          dispatch(getPrevOrNextCase(cursorAPIPayload));
        }
      },
      onLoadFromURLMatch: (params: any) => {
        dispatch(gradeBenchPanelActions.loadCQAItemFromURL(params));
      },
      onCreateCQATransaction: (
        update: Accelerate.CreateCQATransactionParams
      ) => {
        dispatch(gradeBenchPanelActions.createCQATransaction(update))
          .then(() => dispatch(gradeBenchPanelActions.revalidateWorkItem()));
      },
      onDeleteCQATransaction: (
        update: Accelerate.DeleteCQATransactionParams
      ) => {
        dispatch(gradeBenchPanelActions.deleteCQATransaction(update))
          .then(() => dispatch(gradeBenchPanelActions.revalidateWorkItem()));
      },
      submitCQAWorkitem: (
        cursorAPIPayload: CMxCommonApp.CursorAPIPayload,
        status: string = 'CQA_COMPLETE',
        onBeforeSubmit?: () => void
      ) => {
        dispatch(gradeBenchPanelActions.resolveCQAWorkitem())
          .unwrap()
          .then((result) => {
          if (!result?.validationErrors?.length) {
            onBeforeSubmit?.();
            dispatch(
              gradeBenchPanelActions.submitCQAWorkitem({
                cursorAPIPayload,
                status
              })
            );
          }
        });
      },
      fetchAssociatedWorkItems: (workitemId: number) => {
        dispatch(
          gradeBenchPanelActions.retrieveAssociatedWorkitems(workitemId)
        );
      },
      loadAssociatedWorkItem: (
        cursorAPIContext: CMxCommonApp.CursorAPIContext,
        sdWorkitem: CMxAPI.WorkItem
      ) => {
        dispatch(
          gradeBenchPanelActions.launchAssociatedWorkItemInGradeBench({
            cursorAPIContext,
            sdWorkitem
          })
        );
      },
      fetchWorkerInfoForUser: (userContext: CMxCommonApp.Context) => {
        dispatch(gradeBenchPanelActions.fetchWorkerInfoForUser(userContext));
      },
      fetchGraderWorkItemActivity: (sdCaseId: string) => {
        dispatch(gradeBenchPanelActions.getGraderWorkItemActivity(sdCaseId));
      },
    }
  };
};

export const ConnectedServiceDeskAndGradeBench = connect(
  stateMapper,
  dualDispatchMapper
)(ServiceDeskAndGradeBench);
